/**
 * Detail drawer for one affiliate: their application answers, Shopify code,
 * tracked sales, payout history, and the approve / suspend / re-activate /
 * mark-as-paid actions.
 */
import { useCallback, useEffect, useState } from "react";
import { Check, Pause, Play, X } from "lucide-react";
import { Badge, Button, Input, Label, Modal, ModalFooter, Select, useConfirm, useToast } from "@/components/ui";
import { formatDate, formatDateTime } from "@/lib/format";
import { affiliatesApi, type AffiliateDetail as Detail } from "./api";
import { CodeChip } from "./AffiliatesPage";
import { ApprovalEmailEditor, DEFAULT_CREATOR_WELCOME_MESSAGE, DEFAULT_CREATOR_WELCOME_SUBJECT } from "./ApprovalEmailEditor";
import { defaultDiscountCodePreview } from "./code-format";

function usd(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export type DetailAction = "approve" | "payout" | null;

export function AffiliateDetailDrawer({ id, onClose, onChanged, initialAction = null }: { id: string; onClose: () => void; onChanged: () => void; initialAction?: DetailAction }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Approve form state
  const [showApprove, setShowApprove] = useState(false);
  const [ratePct, setRatePct] = useState("10");
  const [codeDraft, setCodeDraft] = useState("");
  const [emailSubject, setEmailSubject] = useState(DEFAULT_CREATOR_WELCOME_SUBJECT);
  const [emailMessage, setEmailMessage] = useState(DEFAULT_CREATOR_WELCOME_MESSAGE);

  // Mark-paid form state
  const [showPayout, setShowPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("zelle");
  const [payoutRef, setPayoutRef] = useState("");

  const toast = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try {
      setDetail(await affiliatesApi.get(id));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    setShowApprove(false);
    setRatePct("10");
    setCodeDraft("");
    setEmailSubject(DEFAULT_CREATOR_WELCOME_SUBJECT);
    setEmailMessage(DEFAULT_CREATOR_WELCOME_MESSAGE);
  }, [id]);

  useEffect(() => {
    if (initialAction === "approve" && detail?.affiliate.status === "pending") {
      setShowApprove(true);
    }
  }, [detail?.affiliate.status, id, initialAction]);

  const a = detail?.affiliate;
  const owed = a ? Number(a.earned) - Number(a.paid) : 0;

  async function run(fn: () => Promise<unknown>, success: string): Promise<boolean> {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      await load();
      onChanged();
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    if (import.meta.env.DEV) {
      toast.error("Final approval is disabled in the test preview. Use the live app when you are ready.");
      return;
    }
    const rate = Number(ratePct) / 100;
    if (!(rate > 0 && rate <= 1)) {
      toast.error("Commission must be between 1% and 100%");
      return;
    }
    if (!emailSubject.trim()) {
      toast.error("Add an email subject before approving");
      return;
    }
    const succeeded = await run(
      () => affiliatesApi.approve(id, {
        commission_rate: rate,
        code: codeDraft.trim() || undefined,
        welcome_email_subject: emailSubject.trim(),
        welcome_email_message: emailMessage.trim(),
      }),
      "Approved - code created and welcome email is being sent",
    );
    if (succeeded) setShowApprove(false);
  }

  async function payout() {
    const amount = Number(payoutAmount);
    if (!(amount > 0)) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    const succeeded = await run(
      () => affiliatesApi.payout(id, { amount, method: payoutMethod, reference: payoutRef.trim() || undefined }),
      `Marked ${usd(amount)} as paid`,
    );
    if (succeeded) setShowPayout(false);
  }

  async function suspend() {
    const ok = await confirm({
      title: `Suspend ${a?.first_name}?`,
      description: "Their discount code stops working in Shopify immediately. Their earned balance is kept.",
      confirmLabel: "Suspend",
      danger: true,
    });
    if (ok) await run(() => affiliatesApi.suspend(id), "Creator suspended and code deactivated");
  }

  async function activate() {
    await run(() => affiliatesApi.activate(id), "Creator re-activated and code working again");
  }

  return (
    <Modal open onClose={onClose} title={a ? `${a.first_name} ${a.last_name}` : "Creator"} size="xl">
      {error ? (
        <p className="text-sm text-red-400">{error}</p>
      ) : !a ? (
        <p className="text-sm text-zinc-500">Loading...</p>
      ) : (
        <div className="space-y-5">
          {/* Status + code + money */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Status" value={<Badge color={a.status === "approved" ? "emerald" : a.status === "pending" ? "amber" : a.status === "suspended" ? "red" : "zinc"}>{a.status}</Badge>} />
            <Stat label="Discount code" value={a.discount_code ? <CodeChip code={a.discount_code} /> : <span className="text-xs text-zinc-400">-</span>} />
            <Stat label="Owed now" value={usd(owed)} strong={owed > 0} />
            <Stat label="Commission" value={`${Math.round(Number(a.commission_rate) * 100)}%`} />
          </div>

          {/* Application answers */}
          <section className="rounded-lg border border-zinc-800/70 p-4 text-sm">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Application</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              <dt className="text-zinc-500">Email</dt><dd className="text-zinc-200 break-all">{a.email}</dd>
              <dt className="text-zinc-500">Socials</dt><dd className="text-zinc-200 break-all"><a className="text-blue-400 hover:underline" href={normalizeLink(a.social_links)} target="_blank" rel="noreferrer">{a.social_links}</a></dd>
              <dt className="text-zinc-500">Previous customer</dt><dd className="text-zinc-200">{boolLabel(a.previous_customer)}</dd>
              <dt className="text-zinc-500">Follows us</dt><dd className="text-zinc-200">{boolLabel(a.follows_social)}</dd>
              {a.dance_context && <><dt className="text-zinc-500">Where they dance</dt><dd className="text-zinc-200">{a.dance_context}</dd></>}
              {a.notes && <><dt className="text-zinc-500">Anything else</dt><dd className="text-zinc-200">{a.notes}</dd></>}
              <dt className="text-zinc-500">Applied</dt><dd className="text-zinc-200">{formatDateTime(a.created_at)}</dd>
              {a.approved_at && <><dt className="text-zinc-500">Approved</dt><dd className="text-zinc-200">{formatDateTime(a.approved_at)}</dd></>}
            </dl>
          </section>

          {/* Tracked sales */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Tracked sales ({detail.sales.length})</h3>
            {detail.sales.length === 0 ? (
              <p className="rounded-lg border border-dashed border-zinc-700 px-4 py-6 text-center text-sm text-zinc-500">
                No sales with their code yet. Orders are picked up automatically every 6 hours, or use "Sync now".
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800/70 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <th className="py-1.5 font-medium">Order</th>
                    <th className="py-1.5 font-medium">Date</th>
                    <th className="py-1.5 text-right font-medium">Sale</th>
                    <th className="py-1.5 text-right font-medium">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.sales.map((s) => (
                    <tr key={s.id} className="border-b border-zinc-800/40 last:border-0">
                      <td className="py-1.5 font-medium text-zinc-200">{s.order_name}</td>
                      <td className="py-1.5 text-zinc-500">{formatDate(s.order_date)}</td>
                      <td className="py-1.5 text-right text-zinc-300">{usd(s.commission_base)}</td>
                      <td className="py-1.5 text-right font-semibold text-emerald-300">{usd(s.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Payout history */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Payouts ({detail.payouts.length})</h3>
            {detail.payouts.length === 0 ? (
              <p className="text-sm text-zinc-500">Nothing paid yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800/70 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <th className="py-1.5 font-medium">Paid</th>
                    <th className="py-1.5 font-medium">Method</th>
                    <th className="py-1.5 font-medium">Reference</th>
                    <th className="py-1.5 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.payouts.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-800/40 last:border-0">
                      <td className="py-1.5 text-zinc-500">{formatDate(p.paid_at)}</td>
                      <td className="py-1.5 text-zinc-300 capitalize">{p.method ?? "-"}</td>
                      <td className="py-1.5 text-zinc-500">{p.reference ?? "-"}</td>
                      <td className="py-1.5 text-right font-medium text-zinc-100">{usd(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Actions */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-zinc-800/70 pt-4">
            <div className="flex gap-2">
              {a.status === "pending" && (
                <Button onClick={() => setShowApprove(true)} disabled={busy}>Approve &amp; create code</Button>
              )}
              {a.status === "approved" && (
                <>
                  <Button onClick={() => { setPayoutAmount(owed > 0 ? String(owed) : ""); setShowPayout(true); }} disabled={busy}>
                    <Check className="h-4 w-4" /> Mark paid{owed > 0 ? ` (${usd(owed)})` : ""}
                  </Button>
                  <Button variant="outline" onClick={suspend} disabled={busy}><Pause className="h-4 w-4" /> Suspend</Button>
                </>
              )}
              {a.status === "suspended" && (
                <Button onClick={activate} disabled={busy}><Play className="h-4 w-4" /> Re-activate</Button>
              )}
            </div>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      )}

      {/* Approve dialog */}
      {showApprove && a && (
        <Modal open onClose={() => setShowApprove(false)} title={`Approve ${a.first_name}`} size="xl">
          <div className="max-h-[calc(100vh-12rem)] space-y-4 overflow-y-auto pr-1">
            <p className="text-sm text-zinc-300">
              Review the welcome email below. Final approval creates their Shopify code (15% off by default, one use per customer), marks them approved, and sends this message with the actual code and link.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Commission rate (%)</Label>
                <Input type="number" min={1} max={100} value={ratePct} onChange={(e) => setRatePct(e.target.value)} />
              </div>
              <div>
                <Label>Discount code (optional - defaults to first name + last initial)</Label>
                <Input value={codeDraft} onChange={(e) => setCodeDraft(e.target.value)} placeholder={defaultDiscountCodePreview(a.first_name, a.last_name)} maxLength={40} />
              </div>
            </div>
            <ApprovalEmailEditor
              to={a.email}
              firstName={a.first_name}
              affiliateType={a.type}
              logoUrl={detail?.brand_logo_url ?? null}
              commissionPct={Math.round(Number(ratePct))}
              customerDiscountPct={15}
              codeDraft={codeDraft}
              subject={emailSubject}
              onSubjectChange={setEmailSubject}
              message={emailMessage}
              onMessageChange={setEmailMessage}
            />
            {import.meta.env.DEV && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
                Test preview only. You can edit and preview here, but final approval is disabled because it creates a Shopify code and sends a real email.
              </div>
            )}
          </div>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setShowApprove(false)}>Cancel</Button>
            <Button onClick={approve} disabled={busy || import.meta.env.DEV || !emailSubject.trim()}>Approve &amp; create code</Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Mark-paid dialog */}
      {showPayout && a && (
        <Modal open onClose={() => setShowPayout(false)} title={`Mark ${a.first_name} as paid`}>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              Records the payout you send them via CashApp, Zelle or similar. Owed right now: <strong>{usd(owed)}</strong>.
            </p>
            <div>
              <Label>Amount</Label>
              <Input type="number" step="0.01" min="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} />
            </div>
            <div>
              <Label>Method</Label>
              <Select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)}>
                <option value="zelle">Zelle</option>
                <option value="cashapp">CashApp</option>
                <option value="other">Other</option>
              </Select>
            </div>
            <div>
              <Label>Reference (optional)</Label>
              <Input value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} placeholder="Confirmation number" maxLength={200} />
            </div>
          </div>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setShowPayout(false)}>Cancel</Button>
            <Button onClick={payout} disabled={busy}>Mark as paid</Button>
          </ModalFooter>
        </Modal>
      )}
    </Modal>
  );
}

function Stat({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) {
  return (
    <div className="rounded-lg border border-zinc-800/70 p-3">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className={`mt-0.5 text-sm ${strong ? "text-lg font-semibold text-emerald-300" : "font-medium text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function boolLabel(v: boolean | null): string {
  return v === null || v === undefined ? "-" : v ? "Yes" : "No";
}

function normalizeLink(raw: string): string {
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}
